import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { PrimaryButton, Modal } from '../../components';
import { Form, FormSpy } from 'react-final-form';
import FieldDateAndTimeInput from '../../components/OrderPanel/BookingTimeForm/FieldDateAndTimeInput';
import css from './RescheduleModal.module.css';

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };
const TODAY = new Date();

const RescheduleModal = props => {
  const {
    intl,
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    heading,
    tx,
    rescheduleInProgress,
    rescheduleError,
    rescheduleSubmitted,
    onFetchTimeSlots,
    monthlyTimeSlots,
    onReschedule,
    dayCountAvailableForBooking,
    timeSlotsForDate,
  } = props;

  const { listing, booking } = tx || {};
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const bookedMins = useMemo(() => {
    if (!booking) return 0;
    return moment
      .duration(
        moment
          .tz(booking.attributes.end, timeZone)
          .diff(moment.tz(booking.attributes.start, timeZone))
      )
      .asMinutes();
  }, [booking, timeZone]);

  const formInitData = useMemo(() => {
    const initData = monthlyTimeSlots ? Object.entries(monthlyTimeSlots) : [];
    const initDate = initData.length ? initData[0][1] : {};
    const firstSlot = initDate?.timeSlots?.[0];
    const startTime = firstSlot?.attributes?.start || null;
    const endTime = firstSlot?.attributes?.end || null;

    return {
      bookingStartDate: { date: startTime },
      bookingEndDate: { date: endTime },
      bookingStartTime: startTime ? moment.tz(startTime, timeZone).valueOf() : null,
      bookingEndTime: startTime
        ? moment
            .tz(startTime, timeZone)
            .add(bookedMins, 'minutes')
            .valueOf()
        : null,
      timeSlotsForDate: initDate.timeSlots,
    };
  }, [monthlyTimeSlots, timeZone, bookedMins]);

  const handleFormChange = ({ values }, form) => {
    const { bookingStartTime, bookingEndTime } = values || {};
    if (bookingStartTime) {
      const newEndTime = moment
        .tz(parseInt(bookingStartTime, 10), timeZone)
        .add(bookedMins, 'minutes')
        .valueOf();

      if (bookingEndTime !== newEndTime) {
        form.change('bookingEndTime', newEndTime);
      }
    }
  };

  const startDatePlaceholder = intl.formatDate(TODAY, dateFormattingOptions);
  const endDatePlaceholder = intl.formatDate(TODAY, dateFormattingOptions);

  return (
    booking && (
      <Modal
        id={id}
        contentClassName={css.modalContent}
        isOpen={isOpen}
        onClose={onClose}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <Form
          onSubmit={onReschedule}
          initialValues={formInitData}
          keepDirtyOnReinitialize
          render={({ form, pristine, handleSubmit, values }) => {
            const startTime = values?.bookingStartTime
              ? parseInt(values.bookingStartTime, 10)
              : null;
            const endTime = values?.bookingEndTime ? parseInt(values.bookingEndTime, 10) : null;

            const bookingMins =
              startTime && endTime
                ? moment.duration(moment(endTime).diff(moment(startTime))).asMinutes()
                : 0;

            const isSameHours = bookedMins === bookingMins;
            const hasUpdatedPeriod =
              startTime &&
              endTime &&
              (startTime !== moment(booking.attributes.start).valueOf() ||
                endTime !== moment(booking.attributes.end).valueOf());

            return (
              <form onSubmit={handleSubmit}>
                <FormSpy
                  onChange={state => handleFormChange(state, form)}
                  subscription={{ values: true }}
                />

                <div className={css.contentWrap}>
                  <h3>{heading}</h3>
                  <div className={css.bookingPeriod}>
                    <div>
                      <h3 className={css.bookingPeriodHeading}>
                        {intl.formatMessage({ id: 'RescheduleModal.bookingPeriod' })}
                      </h3>
                      <div className={css.currentPeriod}>
                        <span>{moment(booking.attributes.start).format('LLL')}</span>
                        <span> - </span>
                        <span>{moment(booking.attributes.end).format('LLL')}</span>
                      </div>
                    </div>
                  </div>

                  {hasUpdatedPeriod && (
                    <div className={css.bookingPeriod}>
                      <div>
                        <h3 className={css.bookingPeriodHeading}>
                          {intl.formatMessage({ id: 'RescheduleModal.newBookingPeriod' })}
                        </h3>
                        <div className={css.currentPeriod}>
                          <span>{moment(startTime).format('LLL')}</span>
                          <span> - </span>
                          <span>{moment(endTime).format('LLL')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={css.dates}>
                    <FieldDateAndTimeInput
                      startDateInputProps={{
                        label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                        placeholderText: startDatePlaceholder,
                      }}
                      endDateInputProps={{
                        label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                        placeholderText: endDatePlaceholder,
                      }}
                      className={css.bookingDates}
                      listingId={listing.id}
                      onFetchTimeSlots={onFetchTimeSlots}
                      monthlyTimeSlots={monthlyTimeSlots}
                      timeSlotsForDate={timeSlotsForDate}
                      values={values}
                      intl={intl}
                      form={form}
                      pristine={pristine}
                      timeZone={timeZone}
                      dayCountAvailableForBooking={dayCountAvailableForBooking}
                      seatsEnabled={false}
                      setSeatsOptions={() => {}}
                      handleFetchLineItems={() => {}}
                      disabledEndTime={true}
                    />
                  </div>

                  {rescheduleError && (
                    <p className={css.error}>
                      {intl.formatMessage({ id: 'RescheduleModal.rescheduleError' })}
                    </p>
                  )}

                  {startTime && endTime && !isSameHours && (
                    <p className={css.error}>
                      {intl.formatMessage({ id: 'RescheduleModal.bookingHoursNotSameError' })}
                    </p>
                  )}

                  <div className={css.confirmButtons}>
                    <PrimaryButton
                      className={css.confirmButton}
                      inProgress={rescheduleInProgress}
                      disabled={rescheduleInProgress || (startTime && endTime && !isSameHours)}
                      ready={rescheduleSubmitted}
                      type="submit"
                    >
                      {intl.formatMessage({ id: 'RescheduleModal.reschedule' })}
                    </PrimaryButton>
                  </div>
                </div>
              </form>
            );
          }}
        />
      </Modal>
    )
  );
};

RescheduleModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  heading: PropTypes.node,
  onReschedule: PropTypes.func,
};

export default RescheduleModal;
